import {memo, type ReactElement, useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import classNames from 'classnames';

import type {FileApiModel} from '@yourcoach/shared/api/media/file';

import {i18n} from '@yourcoach/i18n';
import MoreIcon from '@yourcoach/shared/assets/icons/more.svg';
import {Image} from '@yourcoach/shared/uikit/Image';
import {
  getFileIcon,
  getFileName,
  getFileSrc,
} from '@yourcoach/shared/utils/entity/file';

import Button from '@src/components/Button';
import Checkbox from '@src/components/Checkbox';
import Loader from '@src/components/Loader/Loader';

import styles from './FileCard.module.css';

interface Props<T> {
  className?: string;
  file: T;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  onClick?: (file: T) => void;
  onMoreButtonClick?: (
    file: T,
    event: React.MouseEvent<Element, MouseEvent>,
  ) => void;
}

function FileCard<T extends FileApiModel = FileApiModel>(
  props: Props<T>,
): ReactElement<typeof FileCard> {
  const {
    className,
    file,
    isSelected,
    isSelectionMode,
    onClick,
    onMoreButtonClick,
  } = props;

  const onClickCb: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      onClick?.(file);
    },
    [file, onClick],
  );

  const onMoreButtonClickCb: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      onMoreButtonClick?.(file, e);
    },
    [file, onMoreButtonClick],
  );

  const url = useMemo(() => getFileSrc(file).url, [file]);

  const preview = useMemo(() => {
    if (file.categories.includes('website')) {
      return getFileSrc(file, 'image').url;
    } else if (file.categories.includes('image')) {
      return url;
    } else {
      return null;
    }
  }, [file, url]);

  const {size, urlrepr} = getFileSrc(file);
  const fileName = getFileName(file);

  const Icon = getFileIcon(file);

  const isImageFile = !!file.categories.includes('image');

  return (
    <div
      className={classNames(
        styles.container,
        isImageFile && styles.image,
        className,
      )}
      onClick={onClickCb}
    >
      <div className={styles.content}>
        <div className={styles.previewContainer}>
          <Image
            placeholder={
              <div className={styles.previewPlaceholder}>
                {isImageFile ? <Loader size={75} /> : <Icon />}
              </div>
            }
            src={preview}
          />
        </div>
        {isImageFile ? <div className={styles.gradient} /> : null}
        <div className={styles.dataContainer}>
          <p className={styles.name}>{fileName}</p>
          {size && file.type !== 'url' && !isImageFile ? (
            <p className={styles.text}>{i18n.fileSize(size)}</p>
          ) : null}
          {urlrepr ? (
            <p className={styles.text}>
              {urlrepr.replace(/^((ht|f)tp(s?):\/\/)/, '')}
            </p>
          ) : null}
        </div>
      </div>
      {isSelectionMode && !onMoreButtonClick ? (
        <div className={styles.checkboxContainer}>
          <Checkbox
            checked={isSelected}
            // TODO: Add round checkbox varian
            // variant="round"
          />
        </div>
      ) : null}
      {onMoreButtonClick && !isSelectionMode ? (
        <Button className={styles.moreButton} onClick={onMoreButtonClickCb}>
          <MoreIcon />
        </Button>
      ) : null}
    </div>
  );
}

export default memo(FileCard, isEqual) as typeof FileCard;
